import {Box, Chip, Grid, IconButton, Paper, Skeleton, Stack, Typography, useMediaQuery, useTheme} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, {useContext, useState} from "react";
import CloseIcon from "@mui/icons-material/Close";
import {ThemeContext} from "../../context/ThemeContext";

const DemoResponse = ({response, closeDemoResponse}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [isExpanded, setIsExpanded] = useState(false);

    const {isDarkMode} = useContext(ThemeContext);
    const colors = !isDarkMode ? ["#f5f5f5", "#000000"] : ["#6e6e6e", "#ffffff"];

    const getDisplayName = () => {
        if (response?.is_corporate) {
            return response.company_name || "Corporate Account";
        } else if (response.fname === 'N/A' && response.lname === 'N/A') {
            return "Unknown Customer"
        }
        return `${response?.fname || ""} ${response?.lname || ""}`.trim();
    };

    const toggleExpanded = () => setIsExpanded(!isExpanded);

    return response ? (
        <Paper sx={{width: '100%', mb: 2, mt: 4, p: 2}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 2}}>
                <Typography variant="h6">Customer Details</Typography>
                <IconButton onClick={closeDemoResponse}>
                    <CloseIcon/>
                </IconButton>
            </Stack>

            {/* Summary Section - Always Visible */}
            <Box sx={{mb: 3, backgroundColor: colors[0], color: colors[1], borderRadius: 1, p: 2}}>
                <Typography variant="subtitle2" gutterBottom sx={{fontWeight: 'bold'}}>
                    Summary
                </Typography>
                <Typography variant="body2">{response.summary}</Typography>
            </Box>

            <Grid container spacing={2} sx={{mb: 2}}>
                {/* Main Chips Row */}
                <Grid item xs={12}>
                    <Grid container spacing={1} justifyContent="space-between">
                        <Grid item xs={12} sm={5}>
                            <Chip
                                label={
                                    <Box sx={{
                                        maxWidth: 300,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {getDisplayName()}
                                    </Box>
                                }
                                variant="outlined"
                                sx={{width: '100%', p: 1}}
                            />
                        </Grid>

                        {[
                            {label: "Intent", value: `Intent: ${response.intent}`},
                            {label: "Sentiment", value: `Sentiment: ${response.sentiment}`},
                            {label: "Contract No.", value: `Contract No.: ${response.contract_number}`},
                        ].map((item, index) => (
                            <Grid item xs={4} sm={2} key={index}>
                                <Chip
                                    label={
                                        <Box sx={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {item.value}
                                        </Box>
                                    }
                                    variant="outlined"
                                    sx={{width: '100%'}}
                                />
                            </Grid>
                        ))}

                        <IconButton
                            onClick={toggleExpanded}
                        >
                            {isExpanded ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                        </IconButton>

                    </Grid>
                </Grid>
            </Grid>

            {isExpanded && (
                <Box sx={{mt: 2, borderTop: '1px solid', borderColor: 'divider', pt: 2}}>
                    <Grid container spacing={2}>
                        {/* Original Email Section */}
                        <Grid item xs={12}>

                            <Box sx={{mb: 3, backgroundColor: colors[0], color: colors[1], borderRadius: 1, p: 2}}>
                                <Typography variant="subtitle2" gutterBottom sx={{fontWeight: 'bold'}}>
                                    Original Email
                                </Typography>
                                <Typography variant="body2">{response.originalEmail}</Typography>
                            </Box>
                        </Grid>

                        {/* Contact Information */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom sx={{fontWeight: 'bold'}}>
                                Contact Information
                            </Typography>
                            <Grid container spacing={1}>
                                {[
                                    {
                                        label: "Address",
                                        value: `${response.street_address}, ${response.postal_code} ${response.city}`
                                    },
                                    {label: "Phone", value: response.phone_number},
                                    {label: "Meter No.", value: response.meter_number},
                                ].map((item, index) => (
                                    <Grid item xs={12} key={index}>
                                        <Chip
                                            label={`${item.label}: ${item.value}`}
                                            variant="filled"
                                            color="default"
                                            sx={{width: '100%', justifyContent: 'flex-start'}}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>

                        {/* Account Details */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom sx={{fontWeight: 'bold'}}>
                                Account Details
                            </Typography>
                            <Grid container spacing={1}>
                                {[
                                    {label: "Bank Account", value: response.bank_account},
                                    {label: "Corporate Account", value: response.is_corporate ? "Yes" : "No"},
                                    {label: "Other Contracts", value: response.other_contract_numbers},
                                ].map((item, index) => (
                                    <Grid item xs={12} key={index}>
                                        <Chip
                                            label={`${item.label}: ${item.value}`}
                                            variant="filled"
                                            color="default"
                                            sx={{width: '100%', justifyContent: 'flex-start'}}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
            )}
        </Paper>
    ) : (
        <Paper sx={{width: '100%', mb: 2, mt: 4, p: 2}}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Skeleton variant="rectangular" width={200} height={30} sx={{mb: 2}}/>
                </Grid>

                {[...Array(4)].map((_, index) => (
                    <Grid item xs={12} sm={3} key={index}>
                        <Skeleton variant="rounded" width="100%" height={40}/>
                    </Grid>
                ))}

                <Grid item xs={12} sm={1}>
                    <Skeleton variant="rounded" width="100%" height={40}/>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default DemoResponse;